
import {mergeMap, map, filter} from 'rxjs/operators';
import { Component, OnInit, DoCheck } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, Params } from '@angular/router';
import { CSSMonitorService } from './shared/services/css-monitor.service';
import { Subscription } from 'rxjs';
import { SessionService } from './shared/services/session.service';
import { TokenService } from './shared/services/token.service';
import { Title }     from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';

/**
 * This class represents the main application component. Within the @Routes annotation is the configuration of the
 * applications routes, configuring the paths for the lazy loaded components (HomeComponent, AboutComponent).
 */
@Component({
    selector: 'sd-app',
    templateUrl: 'app.component.html',
    standalone: false
})

export class AppComponent implements OnInit, DoCheck {
  private subscription: Subscription = null;
  public cssDisabled:boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router : Router,
    private translateService: TranslateService,
    private cssMonitorService: CSSMonitorService,
    private session: SessionService,
    private tokenService: TokenService,
    private titleService: Title
  ) {
    
    // kick off translation
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translateService.setDefaultLang('en');

    this.route.queryParams.subscribe((params: Params) => {
      // get current language if specified in the querystring params
      let lang = params['lang'];

      // if there is no language in the query params lets check if there is one in the session
      if (lang === undefined && this.session.lang !== undefined) {
        lang = this.session.lang;
      }
    
      // find the browsers default language
      let browserLang = this.translateService.getBrowserLang();

      // use either language code passed in querystring, browserLang, or default language
      // in that order of preference
      this.translateService.use(lang? lang : browserLang); 
    });

    // if language changes, then change the lang attribute
    this.translateService.onLangChange.subscribe((event : any)=> {
       let htmlTag = document.getElementsByTagName("html");
       if(htmlTag && htmlTag.length > 0){
         htmlTag[0].setAttribute('lang', event.lang);
       }
    });
  }

  ngOnInit(): void {
    this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.route),
    map((route) => {
      while (route.firstChild) route = route.firstChild;
      return route;
    }),
    filter((route) => route.outlet === 'primary'),
    mergeMap((route) => route.data),)
    .subscribe((event) => this.setTitle(event['title']));

    if (environment.underMaintenance) {
        this._showMaintenanceNotice();
    }
    else {
        this.router.initialNavigation();
        this.tokenService.init();
        this.cssMonitorService.start();
        this.subscription = this.cssMonitorService.styleChangeEvent.subscribe(
            (gotCss:string) => {
                if (gotCss === this.cssMonitorService.CSS_DISABLED) {
                    this.handleCSSDisabled();
                } else {
                    this.handleCSSEnabled();
                }
            }
        );
    }
  }

  ngDoCheck(){
    // this was needed to prevent the user from hitting the back 
    // button and gaining access to the underlying site
    if (environment.underMaintenance) {
      this._showMaintenanceNotice();    
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private _showMaintenanceNotice(){
    this.router.navigate(['under-maintenance']);
  }

  protected handleCSSDisabled() {
		this.cssDisabled = true;
	}

	protected handleCSSEnabled() {
		this.cssDisabled = false;
	}

  protected setTitle( newTitle: string ) {
    if (newTitle) {
      this.translateService.get(newTitle).subscribe(res=> {
        this.titleService.setTitle( 'TTP ' + res );
      });
    } else {
      this.titleService.setTitle( 'Trusted Traveler Programs' );
    }
  }
}
